import React from "react";
import { Route } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";
import DashboardPage from "@/modules/stochastic/features/DashboardPage/components/DashboardPage/DashboardPage";
import { ROIDashboardPage } from "@/modules/stochastic/features/ROIDashboard/components/ROIDashboardPage/ROIDashboardPage";
import CustomerList from "../../modules/stochastic/features/CustomerList/components/CustomerList/CustomerList";
import InvoiceList from "../../modules/stochastic/features/InvoiceList/components/InvoiceList/InvoiceList";
import ProspectList from "../../modules/stochastic/features/ProspectList/components/ProspectList/ProspectList";
import DoNotMailList from "@/modules/stochastic/features/DoNotMailManagement/components/DoNotMailList/DoNotMailList";
import CampaignList from "../../modules/stochastic/features/CampaignManagement/components/CampaignList/CampaignList";
import BatchProspectList from "../../modules/stochastic/features/BatchProspectManagement/components/BatchProspectList/BatchProspectList";
import CampaignBatchList from "../../modules/stochastic/features/CampaignBatchManagement/components/CampaignBatchList/CampaignBatchList";
import CampaignBillingList from "../../modules/stochastic/features/CampaignBillingManagement/components/CampaignBillingList/CampaignBillingList";
import CampaignFilesList from "../../modules/stochastic/features/CampaignFileManagement/components/CampaignFilesList/CampaignFilesList";
import { FieldServiceImport } from "@/modules/stochastic/features/FieldServiceImport/components/FieldServiceImport/FieldServiceImport";
import { ProspectSourceImport } from "@/modules/stochastic/features/ProspectSourceImport/components/ProspectSourceImport/ProspectSourceImport";
import ImportStatus from "@/modules/stochastic/features/ImportStatus/components/ImportStatus/ImportStatus";
import CreateCampaignPage from "@/modules/stochastic/features/CampaignManagement/components/CreateCampaignPage/CreateCampaignPage";
import ViewCampaignDetailsPage from "@/modules/stochastic/features/CampaignManagement/components/ViewCampaignDetailsPage/ViewCampaignDetailsPage";
import CloneCampaignPage from "@/modules/stochastic/features/CampaignManagement/components/CloneCampaign/CloneCampaignPage";
import CampaignProductList from "@/modules/stochastic/features/CampaignProductManagement/components/CampaignProductList/CampaignProductList";
import { PostageExpenseImport } from "@/modules/stochastic/features/PostageExpenseImport/components/PostageExpenseImport/PostageExpenseImport";
import LocationsList from "@/modules/stochastic/features/LocationsList/components/LocationList/LocationsList";
import { DoNotMailListImport } from "@/modules/stochastic/features/DoNotMailImport/components/DoNotMailListImport/DoNotMailListImport";
import CampaignStatusPage from "@/modules/administration/features/Stochastic/CampaignStatus/components/CampaignStatusPage/CampaignStatusPage";
import CampaignJobStatusPage from "@/modules/administration/features/Stochastic/CampaignJobStatus/components/CampaignJobStatusPage/CampaignJobStatusPage";

const StochasticMainLayout = () => <MainLayout section="stochastic" />;

const StochasticRoutes = (
  <Route
    element={<AuthenticationGuard component={StochasticMainLayout} />}
    path="/stochastic"
  >
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-dashboard"]}>
          <DashboardPage />
        </PermissionGuard>
      }
      index
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-roi-dashboard"]}>
          <ROIDashboardPage />
        </PermissionGuard>
      }
      path="roi-dashboard"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-customers"]}>
          <CustomerList />
        </PermissionGuard>
      }
      path="customers"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-invoices"]}>
          <InvoiceList />
        </PermissionGuard>
      }
      path="invoices"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-prospects"]}>
          <ProspectList />
        </PermissionGuard>
      }
      path="prospects"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-locations"]}>
          <LocationsList />
        </PermissionGuard>
      }
      path="locations"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <DoNotMailList />
        </PermissionGuard>
      }
      path="do-not-mail"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-campaign-management"]}>
          <CampaignList />
        </PermissionGuard>
      }
      path="campaigns"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignStatusPage />
        </PermissionGuard>
      }
      path="campaign-status"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignJobStatusPage />
        </PermissionGuard>
      }
      path="campaign-job-status"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignBillingList />
        </PermissionGuard>
      }
      path="campaigns/billing"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-campaign-management"]}>
          <CampaignBatchList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/batches"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-campaign-management"]}>
          <BatchProspectList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/batches/:batchId/prospects"
    />

    {/* Files, FieldServiceImport, ProspectSourceImport require stochastic-uploads permission */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignFilesList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/files"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <FieldServiceImport />
        </PermissionGuard>
      }
      path="field-service-import"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <ProspectSourceImport />
        </PermissionGuard>
      }
      path="prospect-source-import"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <DoNotMailListImport />
        </PermissionGuard>
      }
      path="do-not-mail-import"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <ImportStatus />
        </PermissionGuard>
      }
      path="import-status"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-campaign-management"]}>
          <CreateCampaignPage />
        </PermissionGuard>
      }
      path="campaigns/new"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-campaign-management"]}>
          <ViewCampaignDetailsPage />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/view"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-campaign-management"]}>
          <CloneCampaignPage />
        </PermissionGuard>
      }
      path="campaigns/:id/clone"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignProductList />
        </PermissionGuard>
      }
      path="products/campaign"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <PostageExpenseImport />
        </PermissionGuard>
      }
      path="campaigns/postage"
    />
  </Route>
);

export default StochasticRoutes;
